import React from 'react';
import { 
  ArrowLeft, Star, MapPin, Calendar, Package, MessageCircle, 
  Phone, Shield, CheckCircle, Store, Users, Award, Clock,
  Heart, Share2, Camera, ShoppingCart
} from 'lucide-react';
import ReviewItem from '../../components/listing/ReviewItem';

const MobileSellerDetail = ({
  seller,
  sellerName,
  rating,
  totalReviews,
  recentReviews,
  listings,
  filteredListings,
  reviews,
  reviewsLoading,
  activeTab,
  setActiveTab,
  listingsFilter,
  setListingsFilter,
  showAllReviews,
  setShowAllReviews,
  isFavorited,
  handleContactSeller,
  handleShare,
  handleToggleFavorite,
  handleViewListing,
  renderStars,
  formatReviewData,
  formatPrice,
  formatDate,
  getImageUrl,
  calculateAverageRating,
  navigate
}) => {
  const renderStatCard = (icon, label, value, valueColor = "text-black") => (
    <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-gradient-to-br from-orange-100 to-orange-200 rounded-lg text-[#ff660e]">
          {icon}
        </div>
        <div>
          <p className={`text-xl font-bold ${valueColor}`}>
            {typeof value === "number" ? value.toLocaleString() : value}
          </p>
          <p className="text-xs text-gray-600 mt-0.5">{label}</p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="lg:hidden min-h-screen bg-gray-50 pb-20">
      {/* Cover Photo Section */}
      <div className="relative">
        {/* Cover Photo */}
        <div 
          className="w-full h-[150px] bg-cover bg-center relative"
          style={{
            backgroundImage: `url(${seller?.store_cover_picture_url || 'https://via.placeholder.com/1200x300/f5f5f5/cccccc?text=Store+Cover+Photo'})`,
            backgroundSize: 'cover'
          }}
        >
          {/* Overlay */}
          <div className="absolute inset-0 bg-black/30" />
          
          {/* Mobile Header with Back Button and Actions */}
          <div className="absolute top-0 left-0 right-0 p-3">
            <div className="flex items-center justify-between">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center justify-center w-8 h-8 bg-black/50 rounded-full"
              >
                <ArrowLeft className="w-5 h-5 text-white" />
              </button>
              
              <div className="flex items-center gap-2">
                <button 
                  onClick={handleToggleFavorite}
                  className={`flex items-center justify-center w-8 h-8 rounded-full ${
                    isFavorited 
                      ? 'bg-red-500 text-white' 
                      : 'bg-black/50 text-white hover:bg-black/70'
                  }`}
                >
                  <Heart className={`w-4 h-4 ${isFavorited ? 'fill-current' : ''}`} />
                </button>
                <button 
                  onClick={handleShare}
                  className="flex items-center justify-center w-8 h-8 bg-black/50 rounded-full text-white hover:bg-black/70"
                >
                  <Share2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Store Info Overlay - Positioned at bottom of cover */}
        <div className="absolute bottom-0 left-0 right-0 p-4 pb-3">
          <div className="flex items-end">
            {/* Logo */}
            <div className="relative mr-3">
              {seller.store_logo_url ? (
                <img
                  src={getImageUrl(seller.store_logo_url)}
                  alt={sellerName}
                  className="w-16 h-16 rounded-lg border-[3px] border-white shadow-lg object-cover"
                />
              ) : (
                <div className="w-16 h-16 rounded-lg border-[3px] border-white shadow-lg bg-gradient-to-r from-[#ff660e] to-red-500 flex items-center justify-center text-white font-bold text-2xl">
                  {sellerName.charAt(0).toUpperCase()}
                </div>
              )}
              {/* Camera icon for editing (optional) */}
              {seller.is_own_profile && (
                <div className="absolute -bottom-1 -right-1 bg-orange-600 w-5 h-5 rounded-full flex items-center justify-center border-2 border-white">
                  <Camera className="w-2.5 h-2.5 text-white" />
                </div>
              )}
            </div>

            {/* Store Name and Basic Info */}
            <div className="flex-1">
              <h1 className="text-lg font-bold text-white mb-1 drop-shadow-lg">
                {sellerName}
              </h1>
              <div className="flex items-center gap-2">
                <div className="flex items-center bg-black/30 rounded-full px-2 py-0.5">
                  <Star className="w-3 h-3 text-yellow-400 fill-current" />
                  <span className="text-xs text-white ml-1">{rating.toFixed(1)}</span>
                </div>
                <div className="flex items-center bg-black/30 rounded-full px-2 py-0.5">
                  <ShoppingCart className="w-3 h-3 text-white" />
                  <span className="text-xs text-white ml-1">{listings.length} items</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Seller Info Section - Now includes About and Delivery together */}
      <div className="px-4 py-3">
        {/* About This Store */}
        {seller.store_description && (
          <div className="bg-white rounded-xl shadow-sm p-4 mb-3">
            <h4 className="font-medium text-black text-sm mb-2">About This Store</h4>
            <p className="text-gray-700 text-sm leading-relaxed">
              {seller.store_description}
            </p>
            
            {/* Join Date - Moved inside About section */}
            <div className="flex items-center text-gray-500 mt-3 text-xs">
              <Calendar className="w-3 h-3 mr-1" />
              <span>Seller since {formatDate(seller.created_at || seller.user_created_at)}</span>
            </div>
          </div>
        )}

        {/* Delivery Information - Now right below About */}
        {seller.delivery_options && (
          <div className="bg-white rounded-xl shadow-sm p-4 mb-3">
            <h3 className="font-bold text-black mb-3">Delivery Information</h3>
            <div className="space-y-3">
              <div>
                <h4 className="text-sm text-gray-600 mb-1">Delivery Options</h4>
                <p className="text-black text-sm capitalize">{seller.delivery_options}</p>
              </div>
              <div>
                <h4 className="text-sm text-gray-600 mb-1">Delivery Fee</h4>
                <p className="text-black text-sm">
                  {seller.delivery_fee && parseFloat(seller.delivery_fee) > 0 
                    ? `₦${parseFloat(seller.delivery_fee).toLocaleString()}` 
                    : 'Free delivery'}
                </p>
              </div>
              {seller.delivery_coverage_areas && Array.isArray(seller.delivery_coverage_areas) && seller.delivery_coverage_areas.length > 0 && (
                <div>
                  <h4 className="text-sm text-gray-600 mb-1">Coverage Areas</h4>
                  <div className="flex flex-wrap gap-1">
                    {seller.delivery_coverage_areas.map((area, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                      >
                        {area}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          {renderStatCard(
            <Package className="w-4 h-4" />,
            'Listings',
            listings.length
          )}
          {renderStatCard(
            <Users className="w-4 h-4" />,
            'Reviews',
            totalReviews
          )}
          {renderStatCard(
            <Award className="w-4 h-4" />,
            'Rating',
            rating.toFixed(1)
          )}
          {renderStatCard(
            <Clock className="w-4 h-4" />,
            'Response',
            seller?.response_rate ? `${seller.response_rate}%` : 'N/A'
          )}
        </div>

        {/* Tabs */}
        <div className="flex space-x-1 bg-gray-100 p-1 rounded-xl mb-4">
          <button
            onClick={() => setActiveTab('listings')}
            className={`flex-1 py-2.5 px-3 rounded-lg flex items-center justify-center gap-2 transition-colors ${
              activeTab === 'listings'
                ? 'bg-white shadow text-[#ff660e]'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Store className="w-4 h-4" />
            <span className="text-sm font-medium">Listings</span>
          </button>
          <button
            onClick={() => setActiveTab('reviews')}
            className={`flex-1 py-2.5 px-3 rounded-lg flex items-center justify-center gap-2 transition-colors ${
              activeTab === 'reviews'
                ? 'bg-white shadow text-[#ff660e]'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Star className="w-4 h-4" />
            <span className="text-sm font-medium">Reviews</span>
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'listings' ? (
          <div>
            {/* Listings Filter */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <select 
                  value={listingsFilter}
                  onChange={(e) => setListingsFilter(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#ff660e] text-black"
                >
                  <option value="active">Active</option>
                  <option value="featured">Featured</option>
                  <option value="recent">Recent</option>
                </select>
              </div>
              <span className="text-sm text-gray-600">{filteredListings.length} items</span>
            </div>

            {/* Listings Grid */}
            <div className="grid grid-cols-2 gap-3">
              {filteredListings.map((listing) => (
                <div 
                  key={listing.listing_id}
                  className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100"
                  onClick={() => handleViewListing(listing.listing_id)}
                >
                  <div className="aspect-square relative overflow-hidden bg-gray-100">
                    {listing.images && listing.images.length > 0 ? (
                      <img
                        src={getImageUrl(listing.images[0].image_url)}
                        alt={listing.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package className="w-8 h-8 text-gray-400" />
                      </div>
                    )}
                    {listing.is_boosted && (
                      <div className="absolute top-2 left-2 bg-gradient-to-r from-yellow-500 to-[#ff660e] text-white px-2 py-0.5 rounded-full text-xs font-bold">
                        ⚡
                      </div>
                    )}
                  </div>
                  
                  <div className="p-3">
                    <h3 className="font-medium text-black text-sm mb-1 line-clamp-1">
                      {listing.title}
                    </h3>
                    <p className="text-lg font-bold text-[#ff660e]">
                      {formatPrice(listing.price)}
                    </p>
                    <div className="flex items-center text-gray-600 text-xs mt-1">
                      <MapPin className="w-3 h-3 mr-1" />
                      <span className="truncate">{listing.state_name}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {filteredListings.length === 0 && (
              <div className="text-center py-8">
                <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-600">No listings found</p>
              </div>
            )}
          </div>
        ) : (
          /* Reviews Tab */
          <div className="bg-white rounded-xl shadow-sm p-4">
            {reviewsLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#ff660e] mx-auto"></div>
              </div>
            ) : reviews.length === 0 ? (
              <div className="text-center py-6">
                <Star className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-black mb-2">No reviews yet</h3>
                <p className="text-gray-600 text-sm">This seller hasn't received any reviews yet.</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <Star className="w-6 h-6 fill-current text-yellow-400" />
                    <span className="text-2xl font-bold ml-2">{calculateAverageRating()}</span>
                    <span className="text-gray-500 ml-1">/5</span>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-700">{reviews.length} reviews</p>
                    {recentReviews > 0 && (
                      <p className="text-xs text-gray-500">{recentReviews} recent</p>
                    )}
                  </div>
                </div>

                <div className="space-y-4">
                  {(showAllReviews ? reviews : reviews.slice(0, 3)).map((review) => (
                    <div key={review.rating_id} className="border-b border-gray-100 pb-4 last:border-0">
                      <ReviewItem review={formatReviewData(review)} />
                    </div>
                  ))}
                  
                  {reviews.length > 3 && (
                    <button
                      onClick={() => setShowAllReviews(!showAllReviews)}
                      className="w-full border border-gray-300 text-gray-700 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
                    >
                      {showAllReviews ? 'Show Less' : `Show ${reviews.length - 3} More`}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Mobile Bottom Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-3 z-50">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-gray-500">Seller Rating</p>
            <div className="flex items-center">
              {renderStars(rating, 'w-3 h-3')}
            </div>
          </div>
          <div className="flex gap-2">
            {seller.phone_number && (
              <a 
                href={`tel:${seller.phone_number}`}
                className="flex items-center justify-center gap-1 px-3 py-2.5 border-2 border-[#ff660e] text-[#ff660e] rounded-lg text-sm font-semibold"
              >
                <Phone className="w-4 h-4" />
                Call
              </a>
            )}
            <button 
              onClick={handleContactSeller}
              className="flex items-center justify-center gap-1 px-4 py-2.5 bg-gradient-to-r from-[#ff660e] to-red-500 text-white rounded-lg font-semibold text-sm"
            >
              <MessageCircle className="w-4 h-4" />
              Contact
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileSellerDetail;